import React, { useState, useEffect, useRef } from "react";
import { format, addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval } from "date-fns";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Chip,
  MenuItem,
  Pagination,
  Select,
  FormControl,
  InputLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Alert,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import {
  People as PeopleIcon,
  Search as SearchIcon,
  Add as AddIcon,
  FilterList as FilterListIcon,
  Print as PrintIcon,
  Info as InfoIcon,
  NoteAlt as ProgressNoteIcon,
  ExpandMore as ExpandMoreIcon,
  LocalPharmacy as MedicationIcon,
  MonitorHeart as VitalsIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Block as BlockIcon,
  PriorityHigh as PriorityHighIcon,
  Pause as PauseIcon,
  Clear as ClearIcon
} from "@mui/icons-material";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTenant } from "../contexts/TenantContext";
import { useResidents } from "../hooks/useResidents";
import apiClient from "../services/apiClient";
import carePlanService from "../services/carePlanService";
import clinicalNoteService from "../services/clinicalNoteService";
import residentService from "../services/residentService";
import masterDataService from "../services/masterDataService";
import { formatToUIDate } from "../utils/dateUtils";
import { CarePlanDTO } from "../types/careplan.types";
import { ClinicalNoteType } from "../types/clinicalNote.types";
import MedicationAdministrationRecord from "../components/MedicationAdministrationRecord";
import VitalsManager from "../components/VitalsManager";

const ResidentsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { currentTenantId } = useTenant();

  const {
    residents,
    loading,
    error,
    totalPages,
    fetchResidents,
    fetchResidentsByAdmissionType,
    searchResidents,
    quickSearchResidents,
    hasFetched,
    updateResident,
    toggleResidentStatus,
  } = useResidents();

  const userRole = String(user?.role || "").toUpperCase().replace(/^ROLE_/, "");
  const isSuperAdmin = userRole === "SUPER_ADMIN";
  const isAdmin = userRole === "ADMIN" || userRole === "CLIENT_ADMIN";
  const isFacilityManager = userRole === "FACILITY_ADMIN" || userRole === "FACILITY_MANAGER";
  const canToggleStatus = isSuperAdmin || isAdmin || isFacilityManager;

  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [deactivateConfirmOpen, setDeactivateConfirmOpen] = useState(false);
  const [pendingToggleResident, setPendingToggleResident] = useState<any | null>(null);

  const handleToggleStatus = async (resident: any) => {
    if (!resident?.id) return;
    try {
      setTogglingId(resident.id);
      await toggleResidentStatus(resident.id);
    } catch (err) {
      console.error("Failed to update resident status", err);
    } finally {
      setTogglingId(null);
    }
  };

  const handleToggleClick = (resident: any, isCurrentlyActive: boolean) => {
    if (!resident?.id) return;
    if (isCurrentlyActive) {
      setPendingToggleResident(resident);
      setDeactivateConfirmOpen(true);
    } else {
      handleToggleStatus(resident);
    }
  };

  const handleConfirmDeactivate = async () => {
    if (pendingToggleResident) {
      const target = pendingToggleResident;
      setDeactivateConfirmOpen(false);
      setPendingToggleResident(null);
      await handleToggleStatus(target);
    }
  };

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("Active");
  const initialRoleCheckDone = useRef(false);

  useEffect(() => {
    if (!initialRoleCheckDone.current && user?.role) {
      const role = String(user.role).toUpperCase();
      if (role === "ADMIN" || role === "SUPER_ADMIN" || role === "FACILITY_ADMIN" || role === "FACILITY_MANAGER" || role === "CLIENT_ADMIN") {
        setStatusFilter("All");
      }
      initialRoleCheckDone.current = true;
    }
  }, [user?.role]);
  const [typeFilter, setTypeFilter] = useState("All");
  const [statusOptions, setStatusOptions] = useState<{ valueName: string; orderNumber: number }[]>([]);
  const [admissionTypeOptions, setAdmissionTypeOptions] = useState<{ valueName: string; orderNumber: number }[]>([]);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [viewingId, setViewingId] = useState<string | null>(null);
  const [viewingMedicationId, setViewingMedicationId] = useState<string | null>(
    null
  );
  const [viewingOrder, setViewingOrder] = useState<any | null>(null);
  const [carePlanComments, setCarePlanComments] = useState<any[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [commentsError, setCommentsError] = useState<string | null>(null);
  const [commentsModalOpen, setCommentsModalOpen] = useState(false);
  const [carePlanDetails, setCarePlanDetails] = useState<CarePlanDTO | null>(
    null
  );
  const [carePlanLoading, setCarePlanLoading] = useState(false);
  const [carePlanError, setCarePlanError] = useState<string | null>(null);
  const [printingId, setPrintingId] = useState<string | null>(null);
  const [printingDischargeSummaryId, setPrintingDischargeSummaryId] = useState<string | null>(null);
  const [vitalsModalOpen, setVitalsModalOpen] = useState(false);
  const [vitalsResidentId, setVitalsResidentId] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const dischargeSummaryAbortControllerRef = useRef<AbortController | null>(null);

  // Keep stable refs to search functions so the debounce useEffect only
  // depends on `searchTerm` — not on the new useCallback reference created
  // whenever currentTenantId changes (which was firing multiple API calls).
  const fetchResidentsRef = useRef(fetchResidents);
  const quickSearchResidentsRef = useRef(quickSearchResidents);
  useEffect(() => { fetchResidentsRef.current = fetchResidents; }, [fetchResidents]);
  useEffect(() => { quickSearchResidentsRef.current = quickSearchResidents; }, [quickSearchResidents]);

  const [printSectionsModalOpen, setPrintSectionsModalOpen] = useState(false);
  const [printSectionsResidentId, setPrintSectionsResidentId] = useState<string | null>(null);
  const [selectedPrintSections, setSelectedPrintSections] = useState<string[]>([]);

  const printSectionsOptions = [
    { id: 'residentDetails', label: 'Resident Details' },
    { id: 'vitals', label: 'Vitals' },
    { id: 'emrMedications', label: 'EMR Medications' },
    { id: 'diagnosis', label: 'Diagnosis' },
    { id: 'carePlan', label: 'Care Plan' },
    { id: 'medicationOrders', label: 'Medication Orders' },
    { id: 'progressNotes', label: 'Progress Notes' },
    { id: 'appointments', label: 'Appointments' },
    { id: 'documents', label: 'Documents' },
    { id: 'insurance', label: 'Insurance' },
    { id: 'billing', label: 'Billing' },
  ];

  useEffect(() => {
    const loadLookups = async () => {
      try {
        const [sOptions, aOptions] = await Promise.all([
          masterDataService.getActiveLookupValuesByName('Residentpatientstatus'),
          masterDataService.getActiveLookupValuesByName('admissionType'),
        ]);

        let filteredStatusOptions = sOptions;
        const userRole = String(user?.role || "").toUpperCase().replace(/^ROLE_/, "");
        if (userRole === "CAREGIVER" || userRole === "LEAD_CAREGIVER" || userRole === "NURSE") {
          const allowedStatuses = ["active", "hospital appointment", "hospital_appointment", "with family", "with_family", "transferred"];
          filteredStatusOptions = sOptions.filter(opt => allowedStatuses.includes((opt.valueName || "").toLowerCase()));
        }

        setStatusOptions(filteredStatusOptions);
        setAdmissionTypeOptions(aOptions);
      } catch (err) {
        console.error('Failed to load lookup options', err);
      }
    };
    loadLookups();
  }, [user?.role]);

  useEffect(() => {
    if (!currentTenantId) return;

    const delayDebounceFn = setTimeout(() => {
      if (typeFilter === "All") {
        quickSearchResidentsRef.current(searchTerm, statusFilter);
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, statusFilter, typeFilter, currentTenantId]);

  // Handle incoming navigation state for filters
  useEffect(() => {
    const locState = location.state as any;
    if (locState?.filterStatus) {
      if (locState.filterStatus === 'all') {
        setStatusFilter("All");
      } else if (locState.filterStatus === 'active') {
        setStatusFilter("Active");
      }
      // Reset location state after applying filter to prevent sticky behavior
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);

  const calculateAge = (dob: string): number => {
    if (!dob || dob === "N/A") return 0;
    const birth = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birth.getDate())
    ) {
      age--;
    }
    return age;
  };

  const filteredResidents = residents.filter((resident) => {
    // Role-based status visibility restriction
    const userRole = String(user?.role || "").toUpperCase().replace(/^ROLE_/, "");
    const isCaregiver = userRole === "CAREGIVER" || userRole === "LEAD_CAREGIVER" || userRole === "NURSE";
    
    if (isCaregiver) {
      const resStatus = (resident.status || "active").toLowerCase().replace(/_/g, " ");
      const allowedStatuses = ["active", "hospital appointment", "with family", "transferred"];
      if (!allowedStatuses.includes(resStatus)) {
        return false;
      }
    }

    // Since we use the backend quickSearch API which handles all these fields,
    // we bypass local search and status filtering for 'All' types to prevent hiding valid backend results.
    const term = searchTerm.trim().toLowerCase();
    let matchesSearch = true;
    let matchesStatus = true;

    if (typeFilter !== "All") {
      matchesStatus = statusFilter === "All" || resident.status?.toLowerCase() === statusFilter.toLowerCase();
      matchesSearch = term === "" || (
        (resident.fullName?.toLowerCase() || "").includes(term) ||
        (resident.firstName?.toLowerCase() || "").includes(term) ||
        (resident.lastName?.toLowerCase() || "").includes(term) ||
        (resident.emrNumber?.toLowerCase() || "").includes(term) ||
        (resident.roomNumber?.toLowerCase() || "").includes(term)
      );
    }

    const matchesType = true;

    return matchesStatus && matchesSearch && matchesType;
  });

  // Calculate pagination
  const totalLocalPages = Math.max(1, Math.ceil(filteredResidents.length / rowsPerPage));
  // If we are searching, the results might already be the whole list (non-paginated from quick-search)
  // or they could be paginated. The quickSearch implementation in hook currently sets totalPages to 1.
  const paginatedResidents = filteredResidents.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  // Helper to map resident into patient banner shape used by clinical notes
  const buildPatientDetails = (res: any) =>
    res
      ? {
        id: res.id,
        name: res.fullName || res.residentName || `${res.firstName || ""} ${res.middleName ? res.middleName + " " : ""}${res.lastName || ""}`.trim() || "N/A",
        EMRNo:
          (res as any).mrnNumber ||
          (res as any).medicalRecordNumber ||
          (res as any).emrNumber ||
          (res as any).medicareNumber ||
          (res as any).medicaidNumber ||
          res.id ||
          "N/A",
        bp: (res as any).bloodPressure || (res as any).bp || "N/A",
        weight: (res as any).weight || "N/A",
        height: (res as any).height || "N/A",
        bmi: (() => {
          if ((res as any).bmi) return (res as any).bmi;
          // Calculate BMI
          const w = parseFloat((res as any).weight);
          let h = parseFloat((res as any).height);
          if (!w || !h) return "N/A";

          // Normalize height to meters
          // Assumption: If height < 10, it's feet. If > 10, it's cm.
          if (h > 10) {
            h = h / 100; // cm to m
          } else {
            h = h * 0.3048; // ft to m
          }

          const bmiVal = w / (h * h);
          return isFinite(bmiVal) ? bmiVal.toFixed(1) : "N/A";
        })(),
        room:
          res.roomNumber && res.bedNumber
            ? `${res.roomNumber}-${res.bedNumber}`
            : res.roomNumber || "Not Assigned",
        sex: (res as any).gender || "N/A",
        status: (res as any).status || "ACTIVE",
        dob: res.dateOfBirth || undefined,
        attendingPhysician:
          (res as any).attendingPhysicianName ||
          (res as any).attendingPhysician ||
          "N/A",
        allergies: (res as any).allergies
          ? String((res as any).allergies)
            .split(",")
            .map((a: string) => a.trim())
            .filter(Boolean)
          : [],
        admissionType: (res as any).admissionType || res.careType || "N/A",
        bloodGroup: (res as any).bloodGroup || "N/A",
        age: (res as any).age ?? undefined,
      }
      : undefined;

  const handleOpenMAR = (resident: any) => {
    const patientDetails = buildPatientDetails(resident);

    navigate("/clinical-notes", {
      state: {
        residentId: resident.id,
        view: "facesheet",
        patientDetails,
      },
    });
  };

  const handleDiagnosis = (resident: any) => {
    const patientDetails = buildPatientDetails(resident);

    navigate("/patient-diagnosis", {
      state: { residentId: resident.id, patientDetails },
    });
  };

  const handleCancelPrint = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setPrintingId(null);
  };

  const handleCancelDischargeSummary = () => {
    if (dischargeSummaryAbortControllerRef.current) {
      dischargeSummaryAbortControllerRef.current.abort();
      dischargeSummaryAbortControllerRef.current = null;
    }
    setPrintingDischargeSummaryId(null);
  };

  const handlePrintClick = (id: string) => {
    if (printingId === id) {
      handleCancelPrint();
      return;
    }
    setPrintSectionsResidentId(id);
    setSelectedPrintSections([]); // No sections selected by default
    setPrintSectionsModalOpen(true);
  };

  const executePrintResidentWithSections = async (id: string, sections: string[]) => {
    if (printingId) {
      handleCancelPrint();
    }

    setPrintingId(id);

    // Create new AbortController
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const blob = await residentService.getResidentPdfWithSections(id, sections, controller.signal);

      // Check if aborted before continuing
      if (controller.signal.aborted) return;

      const url = window.URL.createObjectURL(blob);

      // Remove any existing print iframe to prevent memory leaks
      const existingIframe = document.getElementById("print-iframe-sections");
      if (existingIframe && document.body.contains(existingIframe)) {
        document.body.removeChild(existingIframe);
      }

      // Create a hidden iframe.
      const iframe = document.createElement("iframe");
      iframe.id = "print-iframe-sections";
      iframe.style.visibility = "hidden";
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "1px";
      iframe.style.height = "1px";
      iframe.style.border = "0";
      iframe.src = url;
      document.body.appendChild(iframe);

      iframe.onload = () => {
        // Double check if aborted before triggering print
        if (controller.signal.aborted) {
          if (document.body.contains(iframe)) {
            document.body.removeChild(iframe);
          }
          window.URL.revokeObjectURL(url);
          return;
        }

        setPrintingId(null);
        abortControllerRef.current = null;

        // Delay to allow PDF viewer to initialize before printing
        setTimeout(() => {
          try {
            if (iframe.contentWindow) {
              iframe.contentWindow.focus();
              iframe.contentWindow.print();
            }
          } catch (printErr) {
            console.error("Direct print failed:", printErr);
          }
        }, 500);
      };

      // Safety timeout to clear spinner if onload never fires
      setTimeout(() => {
        if (printingId === id) {
          setPrintingId(null);
          abortControllerRef.current = null;
        }
      }, 10000);

      // Revoke URL after a safe amount of time, keep iframe in DOM
      // so the print dialog doesn't close immediately.
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 300000); // 5 minutes

    } catch (err: any) {
      if (err.name === 'AbortError') {
        console.log("Print request cancelled");
      } else {
        console.error("Failed to print resident PDF:", err);
      }
      setPrintingId(null);
      abortControllerRef.current = null;
    }
  };

  const handlePrintDischargeSummary = async (id: string) => {
    // Global cancellation: if anything is printing, cancel it first.
    if (printingDischargeSummaryId) {
      handleCancelDischargeSummary();
      // If it was the SAME resident, we just stop. 
      if (printingDischargeSummaryId === id) return;
    }

    setPrintingDischargeSummaryId(id);

    // Create new AbortController
    const controller = new AbortController();
    dischargeSummaryAbortControllerRef.current = controller;

    try {
      const blob = await residentService.getDischargeSummaryPdf(id, controller.signal);

      // Check if aborted before continuing
      if (controller.signal.aborted) return;

      const url = window.URL.createObjectURL(blob);

      // Remove any existing print iframe
      const existingIframe = document.getElementById("print-iframe-discharge");
      if (existingIframe && document.body.contains(existingIframe)) {
        document.body.removeChild(existingIframe);
      }

      // Create a hidden iframe.
      const iframe = document.createElement("iframe");
      iframe.id = "print-iframe-discharge";
      iframe.style.visibility = "hidden";
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "1px";
      iframe.style.height = "1px";
      iframe.style.border = "0";
      iframe.src = url;
      document.body.appendChild(iframe);

      iframe.onload = () => {
        // Double check if aborted before triggering print
        if (controller.signal.aborted) {
          if (document.body.contains(iframe)) {
            document.body.removeChild(iframe);
          }
          window.URL.revokeObjectURL(url);
          return;
        }

        setPrintingDischargeSummaryId(null);
        dischargeSummaryAbortControllerRef.current = null;

        setTimeout(() => {
          try {
            if (iframe.contentWindow) {
              iframe.contentWindow.focus();
              iframe.contentWindow.print();
            }
          } catch (printErr) {
            console.error("Direct print failed:", printErr);
          }
        }, 500);
      };

      // Safety timeout to clear spinner if onload never fires
      setTimeout(() => {
        if (printingDischargeSummaryId === id) {
          setPrintingDischargeSummaryId(null);
          dischargeSummaryAbortControllerRef.current = null;
        }
      }, 10000);

      // Keep iframe in DOM so dialog doesn't close
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 300000); // 5 minutes

    } catch (err: any) {
      if (err.name === 'AbortError') {
        console.log("Discharge summary print request cancelled");
      } else {
        console.error("Failed to print discharge summary PDF:", err);
      }
      setPrintingDischargeSummaryId(null);
      dischargeSummaryAbortControllerRef.current = null;
    }
  };

  const handlePageChange = (event: unknown, value: number) => {
    setPage(value);
    // fetchResidents handled by useEffect
  };

  const handleRowsPerPageChange = (event: any) => {
    const newSize = Number(event.target.value);
    setRowsPerPage(newSize);
    setPage(1);
  };


  // Fetch comments for care plan
  const fetchCarePlanComments = async (carePlanId: string | null) => {
    if (!carePlanId) {
      setCarePlanComments([]);
      return;
    }
    setCommentsLoading(true);
    setCommentsError(null);
    try {

      setTimeout(() => {
        setCarePlanComments([
          { id: 1, author: "Nurse Joy", text: "Patient is improving." },
          { id: 2, author: "Dr. Smith", text: "Continue current plan." },
        ]);
        setCommentsLoading(false);
      }, 500);
    } catch (err: any) {
      setCommentsError("Failed to load comments.");
      setCommentsLoading(false);
    }
  };

  // Post a new comment
  const handlePostComment = async () => {
    if (!commentText.trim() || !viewingOrder?.carePlanId) return;
    setCommentsLoading(true);
    setCommentsError(null);
    try {

      setCarePlanComments((prev) => [
        ...prev,
        { id: Date.now(), author: "You", text: commentText },
      ]);
      setCommentText("");
      setCommentsLoading(false);
    } catch (err: any) {
      setCommentsError("Failed to post comment.");
      setCommentsLoading(false);
    }
  };

  const handleOpenCarePlan = async (resident: any) => {
    const residentId = resident.id;

    setViewingOrder({
      name: resident.fullName ||
        `${resident.firstName || ""} ${resident.middleName ? resident.middleName + " " : ""}${resident.lastName || ""}`.trim() ||
        "N/A",
      attendingPhysician:
        resident.attendingPhysicianName || resident.attendingPhysician || "N/A",
      status: (() => {
        const s = resident.status ? resident.status.toUpperCase() : "ACTIVE";
        switch (s) {
          case "ACTIVE": return "Active";
          case "OUT_OF_FACILITY": return "Out of Facility";
          case "DISCHARGED": return "Discharged";
          case "HOSPITAL_APPOINTMENT": return "Hospital Appointment";
          case "TRANSFERRED": return "Transferred";
          case "WITH_FAMILY": return "With Family";
          default: return "Other";
        }
      })(),
      carePlan: (resident as any).carePlan || {},
      carePlanId: null,
      residentId: residentId || null,
    });
    setViewingId(residentId || null);


    // Fetch care plan details from API using resident ID
    if (residentId) {
      setCarePlanLoading(true);
      setCarePlanError(null);
      setCarePlanDetails(null);
      try {
        const response = await carePlanService.getCarePlanByResidentId(
          residentId
        );

        let carePlan: any = response;
        if (response && (response as any).data) carePlan = (response as any).data;
        else if (response && (response as any).result) carePlan = (response as any).result;

        // If it's an array, take the latest (last) entry
        if (Array.isArray(carePlan)) {
          carePlan = carePlan.length > 0 ? carePlan[carePlan.length - 1] : null;
        }

        if (carePlan) {
          setCarePlanDetails(carePlan);
          // Update the carePlanId in viewingOrder if we got the care plan
          setViewingOrder((prev: any | null) =>
            prev ? { ...prev, carePlanId: carePlan.id } : prev
          );
          // Fetch comments using the care plan ID
          fetchCarePlanComments(carePlan.id);
        } else {
          setCarePlanError("No care plan found for this resident.");
        }
      } catch (error: any) {
        console.error("Failed to fetch care plan details:", error);
        setCarePlanError(
          error.response?.data?.message ||
          "Failed to load care plan details. Please try again."
        );
      } finally {
        setCarePlanLoading(false);
      }
    } else {
      setCarePlanDetails(null);
      setCarePlanError("No resident ID found.");
    }
  };





  // Normalize care plan fields from API response and resident object so
  // the dialog displays consistent data regardless of source field names.
  const _getText = (val: any, fallback: string) => {
    if (val == null) return fallback;
    if (typeof val === "string") {
      return val.trim() ? val : fallback;
    }
    return String(val);
  };
  // Try to read care plan fields from multiple possible shapes returned by backend
  const getCarePlanValue = (key: keyof CarePlanDTO) => {
    if (!carePlanDetails && !viewingOrder?.carePlan) return undefined;

    // Candidate locations to inspect
    const candidates: any[] = [];

    if (carePlanDetails != null) candidates.push(carePlanDetails);
    if ((carePlanDetails as any)?.data)
      candidates.push((carePlanDetails as any).data);
    if ((carePlanDetails as any)?.result)
      candidates.push((carePlanDetails as any).result);
    if (Array.isArray(carePlanDetails) && carePlanDetails.length > 0)
      candidates.push((carePlanDetails as any)[0]);
    if (viewingOrder?.carePlan) candidates.push(viewingOrder.carePlan);

    for (let c of candidates) {
      if (!c) continue;

      // If candidate has data/result, unwrap it if it's not already handled by outer candidates list
      if (c.data) c = c.data;
      else if (c.result) c = c.result;

      // If it's an array now, take the last element
      if (Array.isArray(c)) {
        c = c.length > 0 ? c[c.length - 1] : null;
      }

      if (!c) continue;

      // prefer direct key
      if (Object.prototype.hasOwnProperty.call(c, key) && c[key] != null) {
        return c[key];
      }
      // also try camelCase / snake_case variations just in case
      const alt = (key as string).replace(/([A-Z])/g, "_$1").toLowerCase();
      if (Object.prototype.hasOwnProperty.call(c, alt) && c[alt] != null) {
        return c[alt];
      }
    }

    return undefined;
  };

  const formatStatus = (status: string) => {
    if (!status) return 'N/A';
    return status
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const normalizedCarePlan = {
    dietary: _getText(
      getCarePlanValue("diagnosticsPlan"),
      "No dietary restrictions recorded."
    ),
    dailyActivities: _getText(
      getCarePlanValue("dailyActivities"),
      "No daily activities recorded."
    ),
    ambulation: _getText(
      getCarePlanValue("ambulation"),
      "No ambulation details recorded."
    ),
    woundCare: _getText(
      getCarePlanValue("woundCare"),
      "No wound care details recorded."
    ),
  };


  return (
    <Box sx={{ pt: 3 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          gap: { xs: 2, sm: 0 },
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box sx={{ typography: 'h1', display: 'flex', alignItems: 'center' }}>
            <PeopleIcon sx={{ fontSize: 36, color: "#48CAE4" }} />
          </Box>
          <Box>
            <Typography
              variant="h5"
              fontWeight="700"
              sx={{ mb: 0.5, color: "#1A2B45" }}
            >
              Residents
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500, fontSize: "0.85rem" }}>
              Manage resident profiles, care plans, and clinical records efficiently
            </Typography>
          </Box>
        </Box>
        {user?.role?.toUpperCase() !== "NURSE" && user?.role?.toUpperCase() !== "CAREGIVER" && user?.role?.toUpperCase() !== "LEAD_CAREGIVER" && (
          <Button
            variant="contained"
            startIcon={
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
            }
            component={Link}
            to="/admissions"
            sx={{ bgcolor: "#48CAE4", color: "#000000", fontWeight: "normal", "&:hover": { bgcolor: "#3ab3cc" } }}
          >
            Add New Resident
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 3, display: "flex", gap: 2, flexWrap: "wrap" }}>
        <TextField
          variant="outlined"
          placeholder="Search by EMR Number, Resident Name, Age, Room Number, or Physician"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setPage(1);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => {
                  setSearchTerm("");
                  setPage(1);
                }}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ flex: 1, minWidth: 250 }}
        />

        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => {
              setStatusFilter(e.target.value as string);
              setPage(1);
            }}
          >
            <MenuItem value="All">All</MenuItem>
            {statusOptions.map((opt) => (
              <MenuItem key={opt.valueName} value={opt.valueName}>{opt.valueName}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>Type</InputLabel>
          <Select
            value={typeFilter}
            label="Type"
            onChange={(e) => {
              const newType = e.target.value as string;
              setTypeFilter(newType);
              setPage(1);
              if (newType !== "All") {
                fetchResidentsByAdmissionType(newType);
              }
            }}
          >
            <MenuItem value="All">All</MenuItem>
            {admissionTypeOptions.map((opt) => (
              <MenuItem key={opt.valueName} value={opt.valueName}>{opt.valueName}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          type="button"
          variant="outlined"
          startIcon={<FilterListIcon />}
          onClick={() => {
            setSearchTerm("");
            setStatusFilter("All");
            setTypeFilter("All");
            setPage(1);
          }}
        >
          Reset
        </Button>
      </Paper>

      <Box sx={{ width: '100%', overflowX: 'auto' }}>
        <TableContainer component={Paper} sx={{ mb: 3 }}>
          <Table sx={{ minWidth: 750 }}>
            <TableHead sx={{
              "& .MuiTableCell-head": {
                color: "#434445ff !important",
              }
            }}>
              <TableRow>
                <TableCell>EMR Number</TableCell>
                <TableCell>Resident Name</TableCell>
                {/* <TableCell>Date of Birth</TableCell> */}
                <TableCell>Age</TableCell>
                <TableCell>Room</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Attending Physician</TableCell>
                {/* <TableCell>Care Level</TableCell> */}
                <TableCell>Intake Date</TableCell>
                <TableCell>Vitals</TableCell>
                <TableCell>Care Plan</TableCell>
                {/* <TableCell>Progress Note</TableCell> */}
                <TableCell>Medication</TableCell>
                <TableCell>Print</TableCell>
                <TableCell>Diagnosis</TableCell>
                {canToggleStatus && <TableCell sx={{ textAlign: "center" }}>Status Toggle</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedResidents.length > 0 ? (
                paginatedResidents.map((resident) => {
                  const rawStatusForCheck =
                    resident.status ||
                    (resident as any).residentStatus ||
                    (resident as any).statusText ||
                    ((resident as any).isActive ? "ACTIVE" : "INACTIVE");
                  const isActiveResident = String(rawStatusForCheck).toUpperCase().replace(/ /g, "_") === "ACTIVE";
                  const isCurrentlyActive = resident.isActive !== undefined ? Boolean(resident.isActive) : isActiveResident;

                  return (
                    <TableRow
                      key={resident.id}
                      hover
                      onClick={() => handleOpenMAR(resident)}
                      sx={{
                        "&:last-child td, &:last-child th": { border: 0 },
                        cursor: "pointer",
                      }}
                    >
                      <TableCell>
                        {(resident as any).emrNumber ||
                          (resident as any).medicalRecordNumber ||
                          "N/A"}
                      </TableCell>
                      <TableCell component="th" scope="row">
                        {resident.fullName || `${resident.firstName || ""} ${resident.middleName ? resident.middleName + " " : ""}${resident.lastName || ""}`.trim() || "N/A"}
                      </TableCell>
                      {/* <TableCell>
                    {resident.dateOfBirth
                      ? format(new Date(resident.dateOfBirth), "dd/MM/yyyy")
                      : "N/A"}
                  </TableCell> */}
                      <TableCell>{resident.age ?? calculateAge(resident.dateOfBirth)}</TableCell>
                      <TableCell>
                        {resident.roomNumber
                          ? resident.bedNumber
                            ? `${resident.roomNumber}-${resident.bedNumber}`
                            : resident.roomNumber
                          : "Not Assigned"}
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const rawStatus =
                            resident.status ||
                            (resident as any).residentStatus ||
                            (resident as any).statusText ||
                            ((resident as any).isActive ? "ACTIVE" : "INACTIVE");

                          const formatStatus = (status: string) => {
                            if (!status) return 'N/A';
                            return status
                              .replace(/_/g, ' ')
                              .toLowerCase()
                              .replace(/\b\w/g, (char) => char.toUpperCase());
                          };

                          // Normalize: "Out of Facility" -> "OUT_OF_FACILITY", "Discharged" -> "DISCHARGED"
                          const normalized = String(rawStatus).toUpperCase().replace(/ /g, "_");

                          const statusColor =
                            normalized === "ACTIVE"
                              ? "success"
                              : normalized === "OUT_OF_FACILITY"
                                ? "warning"
                                : normalized === "HOSPITAL_APPOINTMENT"
                                  ? "info"
                                  : normalized === "DISCHARGED"
                                    ? "error"
                                    // : normalized === "TRANSFERRED"
                                    : normalized === "WITH_FAMILY"
                                      ? "warning"
                                      : normalized === "DECEASED"
                                        ? "error"
                                        : "default";
                          return (
                            <Chip
                              label={formatStatus(rawStatus)}
                              size="small"
                              color={statusColor as any}
                            />
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        {resident.attendingPhysicianName || (resident as any).attendingPhysician || "N/A"}
                      </TableCell>
                      {/* <TableCell>{resident.careLevel || "N/A"}</TableCell> */}
                      <TableCell>
                        {formatToUIDate(resident.admissionDate)}
                      </TableCell>
                      <TableCell sx={{ textAlign: "center" }}>
                        <Tooltip title="View Vitals">
                          <span>
                            <IconButton
                              size="small"
                              sx={{ color: "#48CAE4" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setVitalsResidentId(resident.id || null);
                                setVitalsModalOpen(true);
                              }}
                            >
                              <VitalsIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ textAlign: "center" }}>
                        <Tooltip title="View Care Plan">
                          <span>
                            <IconButton
                              size="small"
                              sx={{ color: "#48CAE4" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenCarePlan(resident);
                              }}
                            >
                              <InfoIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>

                      <TableCell sx={{ textAlign: "center" }}>
                        <Tooltip title="View Medication">
                          <span>
                            <IconButton
                              size="small"
                              sx={{ color: "#48CAE4" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                const patientDetails = buildPatientDetails(resident);
                                navigate("/medication-schedule", {
                                  state: { residentId: resident.id, patientDetails },
                                });
                              }}
                            >
                              <MedicationIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>

                      <TableCell sx={{ textAlign: "center" }}>
                        <Tooltip
                          title={
                            printingId === resident.id
                              ? "Cancel Printing"
                              : "Print Resident PDF"
                          }
                        >
                          <span>
                            <IconButton
                              sx={{ color: printingId === resident.id ? "secondary.main" : "#48CAE4" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handlePrintClick(resident.id || "");
                              }}
                            >
                              {printingId === resident.id ? (
                                <Box
                                  sx={{
                                    position: "relative",
                                    display: "inline-flex",
                                  }}
                                >
                                  <CircularProgress size={20} />
                                  <CloseIcon
                                    sx={{
                                      position: "absolute",
                                      fontSize: "10px",
                                      top: "50%",
                                      left: "50%",
                                      transform: "translate(-50%, -50%)",
                                    }}
                                  />
                                </Box>
                              ) : (
                                <PrintIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ textAlign: "center" }}>
                        <Tooltip title="Open Diagnosis">
                          <span>
                            <IconButton
                              size="small"
                              sx={{ color: "#48CAE4" }}
                              disabled={!isActiveResident}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDiagnosis(resident);
                              }}
                            >
                              <ProgressNoteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                      {canToggleStatus && (
                        <TableCell sx={{ textAlign: "center" }} onClick={(e) => e.stopPropagation()}>
                          {(() => {
                            const rawStatus =
                              resident.status ||
                              (resident as any).residentStatus ||
                              (resident as any).statusText ||
                              ((resident as any).isActive ? "ACTIVE" : "INACTIVE");
                            const normalized = String(rawStatus).toUpperCase().replace(/ /g, "_");
                            const isCanToggle = normalized === "ACTIVE" || normalized === "DEACTIVATED" || normalized === "INACTIVE";

                            const tooltipText = !isCanToggle
                              ? `Toggle is only allowed for Active or Deactivated residents (Current: ${formatStatus(rawStatus)})`
                              : isCurrentlyActive
                                ? "Deactivate Resident"
                                : "Activate Resident";

                            return (
                              <Tooltip title={tooltipText}>
                                <span>
                                  <Switch
                                    checked={isCurrentlyActive}
                                    disabled={togglingId === resident.id || !isCanToggle}
                                    onChange={() => handleToggleClick(resident, isCurrentlyActive)}
                                    size="small"
                                    sx={{
                                      '& .MuiSwitch-switchBase.Mui-checked': {
                                        color: '#48CAE4',
                                      },
                                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                        backgroundColor: '#48CAE4',
                                      },
                                    }}
                                  />
                                </span>
                              </Tooltip>
                            );
                          })()}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={canToggleStatus ? 14 : 13} align="center">
                    {loading || !hasFetched ? "Loading residents..." : "No residents found"}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Pagination Container */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 2, sm: 0 },
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Showing {paginatedResidents.length > 0 ? (page - 1) * rowsPerPage + 1 : 0} to {Math.min(page * rowsPerPage, filteredResidents.length)} of {filteredResidents.length} entries
        </Typography>

        <Pagination
          count={totalLocalPages}
          page={page}
          onChange={handlePageChange}
          variant="outlined"
          shape="rounded"
          size="small"
        />
      </Box>

      <Dialog
        open={!!viewingOrder}
        onClose={() => {
          setViewingOrder(null);
          setViewingId(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: "bold", fontSize: "1.3rem" }}>
          Care Plan Details
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 2 }}>
          {viewingOrder && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Paper sx={{ p: 2, backgroundColor: "#f8f9fa" }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Resident Name
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: "bold", wordBreak: "break-all", whiteSpace: "normal" }}>
                      {carePlanDetails?.residentFullName || viewingOrder?.name}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Physician
                    </Typography>
                    <Typography variant="body1" sx={{ wordBreak: "break-all", whiteSpace: "normal" }}>
                      {viewingOrder?.attendingPhysician}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Status
                    </Typography>
                    <Chip
                      label={viewingOrder?.status.replace("_", " ")}
                      color={
                        viewingOrder?.status === "ACTIVE" ||
                          viewingOrder?.status === "Active"
                          ? "success"
                          : viewingOrder?.status === "OUT_OF_FACILITY" ||
                            viewingOrder?.status === "Out of Facility"
                            ? "warning"
                            : viewingOrder?.status === "HOSPITAL_APPOINTMENT" ||
                              viewingOrder?.status === "Hospital Appointment"
                              ? "info"
                              : viewingOrder?.status === "PENDING" ||
                                viewingOrder?.status === "Pending"
                                ? "info"
                                : viewingOrder?.status === "DISCHARGED" ||
                                  viewingOrder?.status === "Discharged"
                                  ? "error"
                                  : viewingOrder?.status === "TRANSFERRED" ||
                                    viewingOrder?.status === "Transferred"
                                    ? "secondary"
                                    : viewingOrder?.status === "WITH_FAMILY" ||
                                      viewingOrder?.status === "With Family"
                                      ? "warning"
                                      : "default"
                      }
                      size="small"
                    />
                  </Grid>
                </Grid>
              </Paper>

              {carePlanLoading ? (
                <Box display="flex" justifyContent="center" p={3}>
                  <CircularProgress />
                </Box>
              ) : carePlanError ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {carePlanError}
                </Alert>
              ) : (
                <>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: "bold", color: "#0066cc", mt: 2, mb: 2 }}
                  >
                    Care Plan Sections
                  </Typography>

                  <Accordion defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography sx={{ fontWeight: "bold" }}>
                        Dietary Restrictions
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography
                        sx={{
                          whiteSpace: "pre-wrap",
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          overflow: "visible",
                        }}
                      >
                        {normalizedCarePlan.dietary}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>

                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography sx={{ fontWeight: "bold" }}>
                        Daily Activities
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography
                        sx={{
                          whiteSpace: "pre-wrap",
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          overflow: "visible",
                        }}
                      >
                        {normalizedCarePlan.dailyActivities}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>

                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography sx={{ fontWeight: "bold" }}>
                        Ambulation
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography
                        sx={{
                          whiteSpace: "pre-wrap",
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          overflow: "visible",
                        }}
                      >
                        {normalizedCarePlan.ambulation}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>

                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography sx={{ fontWeight: "bold" }}>
                        Wound Care/Skin Check
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography
                        sx={{
                          whiteSpace: "pre-wrap",
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          overflow: "visible",
                        }}
                      >
                        {normalizedCarePlan.woundCare}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </>
              )}

              {/* Comments Modal */}
              <Dialog
                open={commentsModalOpen}
                onClose={() => setCommentsModalOpen(false)}
                maxWidth="sm"
                fullWidth
              >
                <DialogTitle>Care Plan Comments</DialogTitle>
                <DialogContent dividers>
                  {commentsLoading ? (
                    <Box display="flex" justifyContent="center" p={2}>
                      <CircularProgress />
                    </Box>
                  ) : commentsError ? (
                    <Alert severity="error">{commentsError}</Alert>
                  ) : (
                    <Box sx={{ mb: 2 }}>
                      {carePlanComments.length === 0 ? (
                        <Typography>No comments yet.</Typography>
                      ) : (
                        carePlanComments.map((c) => (
                          <Paper key={c.id} sx={{ p: 1, mb: 1 }}>
                            <Typography variant="subtitle2">
                              {c.author}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                whiteSpace: "pre-wrap",
                                wordWrap: "break-word",
                                overflowWrap: "break-word",
                                overflow: "visible",
                              }}
                            >
                              {c.text}
                            </Typography>
                          </Paper>
                        ))
                      )}
                    </Box>
                  )}
                  <TextField
                    label="Add a comment"
                    fullWidth
                    multiline
                    minRows={2}
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    sx={{ mb: 2 }}
                  />
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setCommentsModalOpen(false)}>
                    Close
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handlePostComment}
                    disabled={commentsLoading || !commentText.trim()}
                  >
                    Post Comment
                  </Button>
                </DialogActions>
              </Dialog>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => {
              setViewingOrder(null);
              setViewingId(null);
              setCarePlanDetails(null);
              setCarePlanError(null);
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {
        viewingMedicationId && (
          <Dialog
            open={!!viewingMedicationId}
            onClose={() => setViewingMedicationId(null)}
            maxWidth="xl"
            fullWidth
          >
            <MedicationAdministrationRecord
              residentId={viewingMedicationId}
              onClose={() => setViewingMedicationId(null)}
              readOnly={(() => {
                const res = residents.find(r => r.id === viewingMedicationId);
                if (!res) return true;
                const rawStat = res.status || (res as any).residentStatus || (res as any).statusText || ((res as any).isActive ? "ACTIVE" : "INACTIVE");
                return String(rawStat).toUpperCase().replace(/ /g, "_") !== "ACTIVE";
              })()}
            />
          </Dialog>
        )
      }

      <Dialog
        open={vitalsModalOpen}
        onClose={() => setVitalsModalOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: "bold", m: 0, p: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box component="span">
            {(() => {
              const res = residents.find(r => r.id === vitalsResidentId);
              const name = res ? (res.fullName || `${res.firstName || ""} ${res.lastName || ""}`.trim()) : "";
              return name ? `Vitals For - ${name}` : "Vitals For - ";
            })()}
          </Box>
          <IconButton
            aria-label="close"
            onClick={() => setVitalsModalOpen(false)}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {vitalsResidentId && (
            <VitalsManager
              residentId={vitalsResidentId}
              readOnly={(() => {
                const res = residents.find(r => r.id === vitalsResidentId);
                if (!res) return true;
                const rawStat = res.status || (res as any).residentStatus || (res as any).statusText || ((res as any).isActive ? "ACTIVE" : "INACTIVE");
                return String(rawStat).toUpperCase().replace(/ /g, "_") !== "ACTIVE";
              })()}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVitalsModalOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={printSectionsModalOpen}
        onClose={() => setPrintSectionsModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: "bold", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>Select Sections to Print</span>
          <FormControlLabel
            control={
              <Checkbox
                checked={selectedPrintSections.length === printSectionsOptions.length && printSectionsOptions.length > 0}
                indeterminate={selectedPrintSections.length > 0 && selectedPrintSections.length < printSectionsOptions.length}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedPrintSections(printSectionsOptions.map(opt => opt.id));
                  } else {
                    setSelectedPrintSections([]);
                  }
                }}
              />
            }
            label="Select All"
            sx={{ mr: 0, '& .MuiTypography-root': { fontWeight: 600, fontSize: '0.9rem' } }}
          />
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose which sections to include in the exported resident PDF:
          </Typography>
          <FormGroup>
            {printSectionsOptions.map((option) => (
              <FormControlLabel
                key={option.id}
                control={
                  <Checkbox
                    checked={selectedPrintSections.includes(option.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedPrintSections([...selectedPrintSections, option.id]);
                      } else {
                        setSelectedPrintSections(
                          selectedPrintSections.filter((id) => id !== option.id)
                        );
                      }
                    }}
                  />
                }
                label={option.label}
              />
            ))}
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPrintSectionsModalOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            disabled={selectedPrintSections.length === 0}
            onClick={() => {
              setPrintSectionsModalOpen(false);
              if (printSectionsResidentId) {
                executePrintResidentWithSections(printSectionsResidentId, selectedPrintSections);
              }
            }}
          >
            Print
          </Button>
        </DialogActions>
      </Dialog>

      {/* Deactivate Confirmation Dialog */}
      <Dialog
        open={deactivateConfirmOpen}
        onClose={() => {
          setDeactivateConfirmOpen(false);
          setPendingToggleResident(null);
        }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Confirm Deactivation</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to deactivate resident{" "}
            <strong>
              {pendingToggleResident?.fullName ||
                `${pendingToggleResident?.firstName || ""} ${pendingToggleResident?.lastName || ""}`.trim() ||
                "this resident"}
            </strong>
            ?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setDeactivateConfirmOpen(false);
              setPendingToggleResident(null);
            }}
            variant="outlined"
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDeactivate}
            variant="contained"
            sx={{ bgcolor: "#48CAE4", color: "#000", "&:hover": { bgcolor: "#3ab3cc" } }}
            autoFocus
          >
            Yes, Deactivate
          </Button>
        </DialogActions>
      </Dialog>

    </Box >
  );
};

export default ResidentsPage;